import {
  CreateExerciseBody,
  EquipmentKind,
  Exercise,
  ExerciseCategory,
  ExerciseDifficulty,
  ExerciseListParams,
  ExerciseSummary,
  MuscleGroup,
  SaveWorkoutTemplateBody,
  UpdateExerciseBody,
  WorkoutListResponse,
  WorkoutTemplate,
  WorkoutTemplateListParams,
  WorkoutTemplateStatus,
  WorkoutTemplateSummary,
} from './model'

/**
 * localStorage-backed mock store for the workout domain. Unlike the loyalty
 * mock (derived/read-mostly), this feature is CRUD-heavy, so created and
 * edited records must survive a reload. The store starts EMPTY on purpose —
 * the Exercise Library empty state offers an "Add starter exercises" CTA
 * that calls `seedMockStarterExercises()`.
 */
// v2: single-language `name`/`instructions` (v1 stored nameEn/nameTh) — new key
// so stale v1 payloads can't leak malformed records into the UI.
const STORAGE_KEY = 'fitup.workout.mock.v2'

const delay = (ms = 250) => new Promise((resolve) => setTimeout(resolve, ms))

interface MockState {
  categories: ExerciseCategory[]
  exercises: Exercise[]
  templates: WorkoutTemplate[]
  nextId: number
  starterSeeded: boolean
}

const DEFAULT_CATEGORIES: ExerciseCategory[] = [
  { id: 1, name: 'Strength & Conditioning' },
  { id: 2, name: 'Hypertrophy' },
  { id: 3, name: 'Circuit Training' },
  { id: 4, name: 'Mobility & Stability' },
  { id: 5, name: 'Core Body' },
]

const emptyState = (): MockState => ({
  categories: DEFAULT_CATEGORIES.map((c) => ({ ...c })),
  exercises: [],
  templates: [],
  nextId: 100,
  starterSeeded: false,
})

const loadState = (): MockState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return emptyState()
    const parsed = JSON.parse(raw) as MockState
    if (!Array.isArray(parsed.exercises) || !Array.isArray(parsed.templates)) return emptyState()
    return { ...emptyState(), ...parsed }
  } catch {
    return emptyState()
  }
}

const state: MockState = loadState()

const persist = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // quota / private mode — keep working in-memory
  }
}

const nextId = () => {
  const id = state.nextId
  state.nextId += 1
  return id
}

const now = () => new Date().toISOString()

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T

// ─── Starter seed data ──────────────────────────────────────────────────────

type SeedExercise = [
  name: string,
  categoryId: number,
  muscleGroups: MuscleGroup[],
  equipment: EquipmentKind[],
  difficulty: ExerciseDifficulty,
  youtubeUrl?: string,
  instructions?: string,
]

const { CHEST, BACK, SHOULDERS, BICEPS, TRICEPS, CORE, GLUTES, QUADS, HAMSTRINGS, CALVES } =
  MuscleGroup
const FULL = MuscleGroup.FULL_BODY
const CARDIO = MuscleGroup.CARDIO
const { BODYWEIGHT, BARBELL, DUMBBELL, KETTLEBELL, MACHINE, CABLE, CARDIO_MACHINE, OTHER } =
  EquipmentKind
const { BEGINNER, INTERMEDIATE, ADVANCED } = ExerciseDifficulty

const STARTER_EXERCISES: SeedExercise[] = [
  [
    'Barbell Back Squat',
    1,
    [QUADS, GLUTES],
    [BARBELL],
    INTERMEDIATE,
    'https://www.youtube.com/watch?v=ultWZbUMPL8',
    'วางบาร์บนบ่า เท้ากว้างระดับไหล่ ย่อลงจนต้นขาขนานพื้น แล้วดันขึ้นด้วยกลางเท้า',
  ],
  [
    'Deadlift',
    1,
    [BACK, HAMSTRINGS, GLUTES],
    [BARBELL],
    ADVANCED,
    'https://www.youtube.com/watch?v=op9kVnSso6Q',
    'พับสะโพก จับบาร์นอกเข่าเล็กน้อย หลังตรง แล้วยืดตัวขึ้นยืนตรง',
  ],
  [
    'Bench Press',
    1,
    [CHEST, TRICEPS],
    [BARBELL],
    INTERMEDIATE,
    'https://www.youtube.com/watch?v=rT7DgCr-3pg',
  ],
  ['Overhead Press', 1, [SHOULDERS, TRICEPS], [BARBELL], INTERMEDIATE],
  [
    'Pull-up',
    1,
    [BACK, BICEPS],
    [BODYWEIGHT],
    INTERMEDIATE,
    'https://www.youtube.com/watch?v=eGo4IYlbE5g',
  ],
  [
    'Push-up',
    1,
    [CHEST, TRICEPS, CORE],
    [BODYWEIGHT],
    BEGINNER,
    'https://www.youtube.com/watch?v=IODxDxX7oi4',
    'มือวางใต้ไหล่ ลำตัวตรง หย่อนอกลงใกล้พื้นแล้วดันกลับขึ้น',
  ],
  ['Dumbbell Bicep Curl', 2, [BICEPS], [DUMBBELL], BEGINNER],
  ['Lateral Raise', 2, [SHOULDERS], [DUMBBELL], BEGINNER],
  ['Dumbbell Row', 2, [BACK, BICEPS], [DUMBBELL], BEGINNER],
  ['Leg Press', 2, [QUADS, GLUTES], [MACHINE], BEGINNER],
  ['Lat Pulldown', 2, [BACK, BICEPS], [CABLE], BEGINNER],
  ['Cable Fly', 2, [CHEST], [CABLE], INTERMEDIATE],
  ['Romanian Deadlift', 2, [HAMSTRINGS, GLUTES], [BARBELL], INTERMEDIATE],
  ['Hip Thrust', 2, [GLUTES], [BARBELL], INTERMEDIATE],
  ['Walking Lunge', 3, [QUADS, GLUTES], [BODYWEIGHT, DUMBBELL], BEGINNER],
  [
    'Kettlebell Swing',
    3,
    [GLUTES, HAMSTRINGS, CARDIO],
    [KETTLEBELL],
    INTERMEDIATE,
    'https://www.youtube.com/watch?v=YSxHifyI6s8',
  ],
  ['Burpee', 3, [FULL, CARDIO], [BODYWEIGHT], INTERMEDIATE],
  ['Mountain Climber', 3, [CORE, CARDIO], [BODYWEIGHT], BEGINNER],
  ['Box Jump', 3, [QUADS, CALVES], [OTHER], INTERMEDIATE],
  ['Rowing Machine', 3, [FULL, CARDIO], [CARDIO_MACHINE], BEGINNER],
  [
    'Plank',
    5,
    [CORE],
    [BODYWEIGHT],
    BEGINNER,
    'https://www.youtube.com/watch?v=ASdvN_XEl_c',
    'วางแขนท่อนล่างบนพื้น ลำตัวตรงตั้งแต่หัวถึงส้นเท้า เกร็งหน้าท้องค้างไว้',
  ],
  ['Russian Twist', 5, [CORE], [BODYWEIGHT], BEGINNER],
  ['Hanging Leg Raise', 5, [CORE], [BODYWEIGHT], ADVANCED],
  ['Dead Bug', 5, [CORE], [BODYWEIGHT], BEGINNER],
  ["World's Greatest Stretch", 4, [FULL], [BODYWEIGHT], BEGINNER],
  ['Cat-Cow', 4, [BACK, CORE], [BODYWEIGHT], BEGINNER],
  ['Hip Flexor Stretch', 4, [QUADS], [BODYWEIGHT], BEGINNER],
  ['Foam Rolling Quads', 4, [QUADS], [OTHER], BEGINNER],
]

const seedExerciseRecord = (seed: SeedExercise): Exercise => {
  const [name, categoryId, muscleGroups, equipment, difficulty, youtubeUrl, instructions] = seed
  const ts = now()
  return {
    id: nextId(),
    name,
    categoryId,
    muscleGroups,
    equipment,
    difficulty,
    instructions,
    imageUrls: [],
    youtubeUrl,
    status: 'ACTIVE',
    createdAt: ts,
    updatedAt: ts,
  }
}

const findSeeded = (name: string) => {
  const found = state.exercises.find((e) => e.name === name)
  if (!found) throw new Error(`starter template references missing exercise: ${name}`)
  return found
}

const seedRow = (
  name: string,
  order: number,
  fields: { sets?: number; reps?: string; restSeconds?: number; weight?: string; notes?: string }
) => ({ id: nextId(), exerciseId: findSeeded(name).id, order, ...fields })

const seedStarterTemplates = (): WorkoutTemplate[] => {
  const ts = now()
  const build = (
    name: string,
    difficulty: ExerciseDifficulty,
    durationMinutes: number,
    sections: { title: string; rows: ReturnType<typeof seedRow>[] }[]
  ): WorkoutTemplate => {
    const tree = sections.map((s, i) => ({
      id: nextId(),
      title: s.title,
      order: i,
      exercises: s.rows,
    }))
    return {
      id: nextId(),
      name,
      difficulty,
      durationMinutes,
      targetMuscleGroups: deriveMuscles(tree.flatMap((s) => s.exercises.map((e) => e.exerciseId))),
      status: 'PUBLISHED',
      sections: tree,
      createdAt: ts,
      updatedAt: ts,
    }
  }

  return [
    build('Full Body Beginner', BEGINNER, 45, [
      {
        title: 'Warm-up',
        rows: [
          seedRow('Cat-Cow', 0, { sets: 1, reps: '10', restSeconds: 0 }),
          seedRow('Rowing Machine', 1, { sets: 1, reps: '5 min', restSeconds: 0 }),
        ],
      },
      {
        title: 'Main',
        rows: [
          seedRow('Leg Press', 0, { sets: 3, reps: '12', restSeconds: 90 }),
          seedRow('Push-up', 1, { sets: 3, reps: '10', restSeconds: 60 }),
          seedRow('Lat Pulldown', 2, { sets: 3, reps: '12', restSeconds: 90 }),
          seedRow('Dumbbell Bicep Curl', 3, { sets: 2, reps: '12', restSeconds: 60 }),
          seedRow('Plank', 4, { sets: 3, reps: '30s', restSeconds: 45 }),
        ],
      },
      {
        title: 'Cool-down',
        rows: [seedRow('Hip Flexor Stretch', 0, { sets: 1, reps: '30s each side' })],
      },
    ]),
    build('Upper Body Strength', INTERMEDIATE, 55, [
      {
        title: 'Warm-up',
        rows: [seedRow('Push-up', 0, { sets: 2, reps: '10', restSeconds: 30 })],
      },
      {
        title: 'Main',
        rows: [
          seedRow('Bench Press', 0, { sets: 4, reps: '5', restSeconds: 150, weight: '80% 1RM' }),
          seedRow('Overhead Press', 1, { sets: 4, reps: '6', restSeconds: 120 }),
          seedRow('Pull-up', 2, { sets: 4, reps: 'AMRAP', restSeconds: 120, weight: 'BW' }),
          seedRow('Dumbbell Row', 3, { sets: 3, reps: '8-10', restSeconds: 90 }),
          seedRow('Cable Fly', 4, { sets: 3, reps: '12-15', restSeconds: 60 }),
        ],
      },
      {
        title: 'Cool-down',
        rows: [seedRow("World's Greatest Stretch", 0, { sets: 1, reps: '5 each side' })],
      },
    ]),
    build('HIIT Circuit 30', INTERMEDIATE, 30, [
      {
        title: 'Warm-up',
        rows: [seedRow('Mountain Climber', 0, { sets: 2, reps: '30s', restSeconds: 30 })],
      },
      {
        title: 'Circuit ×4',
        rows: [
          seedRow('Kettlebell Swing', 0, { sets: 4, reps: '15', restSeconds: 30 }),
          seedRow('Burpee', 1, { sets: 4, reps: '10', restSeconds: 30 }),
          seedRow('Box Jump', 2, { sets: 4, reps: '8', restSeconds: 30 }),
          seedRow('Walking Lunge', 3, { sets: 4, reps: '20 steps', restSeconds: 60 }),
        ],
      },
      {
        title: 'Cool-down',
        rows: [seedRow('Cat-Cow', 0, { sets: 1, reps: '10' })],
      },
    ]),
  ]
}

// ─── Shared helpers ─────────────────────────────────────────────────────────

const exerciseById = (id: number) => state.exercises.find((e) => e.id === id)

/** Uploaded image first, else the YouTube thumbnail — same fallback the cards use. */
const exerciseThumb = (exercise?: Exercise): string | undefined => {
  if (!exercise) return undefined
  if (exercise.imageUrls[0]) return exercise.imageUrls[0]
  const id = exercise.youtubeUrl?.match(/(?:v=|youtu\.be\/|shorts\/|embed\/)([\w-]{11})/)?.[1]
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : undefined
}

const toSummary = (exercise: Exercise): ExerciseSummary => ({
  id: exercise.id,
  name: exercise.name,
  thumbnailUrl: exerciseThumb(exercise),
  youtubeUrl: exercise.youtubeUrl,
  difficulty: exercise.difficulty,
  muscleGroups: exercise.muscleGroups,
})

const deriveMuscles = (exerciseIds: number[]): MuscleGroup[] => {
  const seen = new Set<MuscleGroup>()
  exerciseIds.forEach((id) => {
    exerciseById(id)?.muscleGroups.forEach((m) => seen.add(m))
  })
  return Array.from(seen)
}

/** Attach denormalized exercise summaries to a template's rows. */
const hydrateTemplate = (template: WorkoutTemplate): WorkoutTemplate => {
  const copy = clone(template)
  copy.sections.forEach((section) => {
    section.exercises.forEach((row) => {
      const exercise = exerciseById(row.exerciseId)
      row.exercise = exercise ? toSummary(exercise) : undefined
    })
  })
  return copy
}

const paginate = <T>(items: T[], page = 1, pageSize = 24): WorkoutListResponse<T> => ({
  data: items.slice((page - 1) * pageSize, page * pageSize),
  pagination: { page, pageSize, total: items.length },
})

// ─── Categories ─────────────────────────────────────────────────────────────

export const getMockExerciseCategories = async (): Promise<ExerciseCategory[]> => {
  await delay()
  return state.categories.map((c) => ({
    ...c,
    exerciseCount: state.exercises.filter((e) => e.categoryId === c.id && e.status === 'ACTIVE')
      .length,
  }))
}

export const createMockExerciseCategory = async (body: {
  name: string
}): Promise<ExerciseCategory> => {
  await delay()
  const category: ExerciseCategory = { id: nextId(), ...body }
  state.categories.push(category)
  persist()
  return clone(category)
}

// ─── Exercises ──────────────────────────────────────────────────────────────

export const getMockExercises = async (
  params: ExerciseListParams = {}
): Promise<WorkoutListResponse<Exercise>> => {
  await delay()
  const search = params.search?.trim().toLowerCase()
  const status = params.status ?? 'ACTIVE'
  const filtered = state.exercises.filter((e) => {
    if (e.status !== status) return false
    if (params.categoryId && e.categoryId !== params.categoryId) return false
    if (params.muscleGroup && !e.muscleGroups.includes(params.muscleGroup)) return false
    if (params.equipment && !e.equipment.includes(params.equipment)) return false
    if (params.difficulty && e.difficulty !== params.difficulty) return false
    if (search && !e.name.toLowerCase().includes(search)) return false
    return true
  })
  const sorted = [...filtered].sort((a, b) => a.name.localeCompare(b.name))
  return clone(paginate(sorted, params.page, params.pageSize))
}

export const getMockExercise = async (id: number): Promise<Exercise> => {
  await delay()
  const exercise = exerciseById(id)
  if (!exercise) throw new Error('Exercise not found')
  return clone(exercise)
}

export const createMockExercise = async (body: CreateExerciseBody): Promise<Exercise> => {
  await delay()
  const ts = now()
  const exercise: Exercise = {
    id: nextId(),
    ...body,
    status: 'ACTIVE',
    createdAt: ts,
    updatedAt: ts,
  }
  state.exercises.push(exercise)
  persist()
  return clone(exercise)
}

export const updateMockExercise = async (
  id: number,
  body: UpdateExerciseBody
): Promise<Exercise> => {
  await delay()
  const exercise = exerciseById(id)
  if (!exercise) throw new Error('Exercise not found')
  Object.assign(exercise, body, { updatedAt: now() })
  persist()
  return clone(exercise)
}

export const setMockExerciseStatus = async (
  id: number,
  status: Exercise['status']
): Promise<Exercise> => {
  await delay()
  const exercise = exerciseById(id)
  if (!exercise) throw new Error('Exercise not found')
  exercise.status = status
  exercise.updatedAt = now()
  persist()
  return clone(exercise)
}

/** Populate the library with the starter set (and demo templates) — idempotent. */
export const seedMockStarterExercises = async (): Promise<{ seeded: number }> => {
  await delay(400)
  if (state.starterSeeded) return { seeded: 0 }
  state.exercises.push(...STARTER_EXERCISES.map(seedExerciseRecord))
  state.templates.push(...seedStarterTemplates())
  state.starterSeeded = true
  persist()
  return { seeded: STARTER_EXERCISES.length }
}

// ─── Workout templates ──────────────────────────────────────────────────────

const toTemplateSummary = (template: WorkoutTemplate): WorkoutTemplateSummary => {
  const rows = template.sections.flatMap((s) => s.exercises)
  return {
    id: template.id,
    name: template.name,
    description: template.description,
    difficulty: template.difficulty,
    durationMinutes: template.durationMinutes,
    targetMuscleGroups: template.targetMuscleGroups,
    status: template.status,
    exerciseCount: rows.length,
    previewImageUrls: rows
      .map((row) => exerciseThumb(exerciseById(row.exerciseId)))
      .filter((url): url is string => Boolean(url)),
    updatedAt: template.updatedAt,
  }
}

export const getMockWorkoutTemplates = async (
  params: WorkoutTemplateListParams = {}
): Promise<WorkoutListResponse<WorkoutTemplateSummary>> => {
  await delay()
  const search = params.search?.trim().toLowerCase()
  const filtered = state.templates.filter((t) => {
    if (params.status ? t.status !== params.status : t.status === 'ARCHIVED') return false
    if (params.difficulty && t.difficulty !== params.difficulty) return false
    if (params.muscleGroup && !t.targetMuscleGroups.includes(params.muscleGroup)) return false
    if (search && !t.name.toLowerCase().includes(search)) return false
    return true
  })
  const sorted = [...filtered].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  return clone(paginate(sorted.map(toTemplateSummary), params.page, params.pageSize))
}

export const getMockWorkoutTemplate = async (id: number): Promise<WorkoutTemplate> => {
  await delay()
  const template = state.templates.find((t) => t.id === id)
  if (!template) throw new Error('Workout template not found')
  return hydrateTemplate(template)
}

const materializeSections = (body: SaveWorkoutTemplateBody): WorkoutTemplate['sections'] =>
  body.sections.map((section, sIdx) => ({
    id: nextId(),
    title: section.title,
    order: sIdx,
    exercises: section.exercises.map((row, rIdx) => ({
      id: nextId(),
      exerciseId: row.exerciseId,
      order: rIdx,
      sets: row.sets,
      reps: row.reps,
      restSeconds: row.restSeconds,
      weight: row.weight,
      notes: row.notes,
      supersetGroup: row.supersetGroup,
    })),
  }))

export const createMockWorkoutTemplate = async (
  body: SaveWorkoutTemplateBody
): Promise<WorkoutTemplate> => {
  await delay()
  const ts = now()
  const sections = materializeSections(body)
  const template: WorkoutTemplate = {
    id: nextId(),
    name: body.name,
    description: body.description,
    difficulty: body.difficulty,
    durationMinutes: body.durationMinutes,
    targetMuscleGroups: deriveMuscles(
      sections.flatMap((s) => s.exercises.map((e) => e.exerciseId))
    ),
    status: body.status,
    sections,
    createdAt: ts,
    updatedAt: ts,
  }
  state.templates.push(template)
  persist()
  return hydrateTemplate(template)
}

export const updateMockWorkoutTemplate = async (
  id: number,
  body: SaveWorkoutTemplateBody
): Promise<WorkoutTemplate> => {
  await delay()
  const template = state.templates.find((t) => t.id === id)
  if (!template) throw new Error('Workout template not found')
  const sections = materializeSections(body)
  Object.assign(template, {
    name: body.name,
    description: body.description,
    difficulty: body.difficulty,
    durationMinutes: body.durationMinutes,
    targetMuscleGroups: deriveMuscles(
      sections.flatMap((s) => s.exercises.map((e) => e.exerciseId))
    ),
    status: body.status,
    sections,
    updatedAt: now(),
  })
  persist()
  return hydrateTemplate(template)
}

export const setMockWorkoutTemplateStatus = async (
  id: number,
  status: WorkoutTemplateStatus
): Promise<WorkoutTemplate> => {
  await delay()
  const template = state.templates.find((t) => t.id === id)
  if (!template) throw new Error('Workout template not found')
  template.status = status
  template.updatedAt = now()
  persist()
  return hydrateTemplate(template)
}

export const duplicateMockWorkoutTemplate = async (id: number): Promise<WorkoutTemplate> => {
  await delay()
  const source = state.templates.find((t) => t.id === id)
  if (!source) throw new Error('Workout template not found')
  const ts = now()
  const copy: WorkoutTemplate = {
    ...clone(source),
    id: nextId(),
    name: `${source.name} (copy)`,
    status: 'DRAFT',
    sections: source.sections.map((section) => ({
      ...clone(section),
      id: nextId(),
      exercises: section.exercises.map((row) => ({ ...clone(row), id: nextId() })),
    })),
    createdAt: ts,
    updatedAt: ts,
  }
  state.templates.push(copy)
  persist()
  return hydrateTemplate(copy)
}
